import type React from 'react'

/* ── Styles ──────────────────────────────────────────────────── */
export const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh' },
  loadRoot: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', background: '#FAF6EF', color: '#888' },

  // Quiz-style preview in setup
  previewCard: {
    background:   '#FFFFFF',
    border:       '1.5px solid #EFE7D7',
    borderRadius: 18,
    padding:      '24px 20px',
    width:        '100%',
    boxShadow:    '0 4px 20px rgba(27,67,50,0.07)',
    display:      'flex',
    flexDirection: 'column',
    gap:          12,
  },
  previewDefLabel: {
    fontSize: '0.875rem',
    fontWeight:    800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color:         '#9A8070',
  },
  previewDefBox: {
    fontFamily:   "'Cormorant Garamond', serif",
    fontSize:     '1.1rem',
    fontWeight:   600,
    color:        '#2E1F14',
    padding:      '14px 16px',
    background:   '#FAF6EF',
    borderRadius: 10,
    border:       '1.5px solid #EFE7D7',
    lineHeight:   1.4,
  },
  previewOptionsRow: {
    display:             'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap:                 8,
  },
  previewOption: {
    fontSize:     '0.9rem',
    fontWeight:   600,
    color:        '#2E1F14',
    padding:      '10px 12px',
    background:   '#FAF6EF',
    borderRadius: 8,
    border:       '1.5px solid #EFE7D7',
    textAlign:    'center',
  },
  previewOptionCorrect: {
    background: 'linear-gradient(135deg, #52B788, #3D9970)',
    border:     '1.5px solid #2D6A4F',
    color:      '#FFFFFF',
  },

  // ── Play screen ──
  playRoot: {
    minHeight:     '100vh',
    display:       'flex',
    flexDirection: 'column',
    background:    '#FAF6EF',
  },
  // ── Quiz main area ──
  quizMain: {
    flex:          1,
    maxWidth:      800,
    width:         '100%',
    margin:        '0 auto',
    padding:       '32px 20px 48px',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           24,
  },
  counterPill: {
    fontSize: '1.25rem',
    fontWeight:    700,
    color:         '#1B4332',
    background:    '#E8F5EE',
    border:        '1.5px solid #B7E4C7',
    borderRadius:  20,
    padding:       '6px 16px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },

  // Definition card
  defCard: {
    width:         '100%',
    background:    '#FFFFFF',
    border:        '1.5px solid #EFE7D7',
    borderRadius:  20,
    padding:       '32px 28px',
    boxShadow:     '0 4px 24px rgba(27,67,50,0.08)',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           16,
    textAlign:     'center',
  },
  defImage: {
    width:        96,
    height:       96,
    objectFit:    'contain' as const,
    borderRadius: 12,
    background:   '#fff',
    border:       '1px solid #EFE7D7',
    flexShrink:   0,
  },
  defImageLarge: {
    width:        180,
    height:       180,
    objectFit:    'contain' as const,
    borderRadius: 16,
    background:   '#fff',
    border:       '1px solid #EFE7D7',
    flexShrink:   0,
  },
  defEmoji: {
    fontSize:   '5rem',
    lineHeight: 1,
    filter:     'drop-shadow(0 2px 6px rgba(0,0,0,0.12))',
  },

  // Mode selector cards
  modeEmoji: {
    fontSize:   '2rem',
    lineHeight: 1,
  },
  modeLabel: {
    fontSize:   '1.05rem',
    fontWeight: 700,
    color:      '#2E1F14',
  },
  modeDesc: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color:      '#6B5C50',
  },

  // Preview: image mode box
  previewImageBox: {
    fontSize:     '3.5rem',
    lineHeight:   1,
    textAlign:    'center' as const,
    padding:      '20px',
    background:   '#FAF6EF',
    borderRadius: 10,
    border:       '1.5px solid #EFE7D7',
  },

  // Preview: anagram mode scramble
  previewScramble: {
    fontFamily:    "'Cormorant Garamond', serif",
    fontSize:      '1.6rem',
    fontWeight:    700,
    letterSpacing: '0.15em',
    color:         '#1B4332',
    padding:       '16px 20px',
    background:    '#E8F5EE',
    borderRadius:  10,
    border:        '1.5px solid #B7E4C7',
    textAlign:     'center' as const,
  },
  defText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   'clamp(1.25rem, 2.5vw, 1.75rem)',
    fontWeight: 600,
    color:      '#2E1F14',
    lineHeight: 1.5,
  },

  // Anagram scramble display
  scrambleCard: {
    width:         '100%',
    background:    '#E8F5EE',
    border:        '1.5px solid #B7E4C7',
    borderRadius:  16,
    padding:       '20px 24px',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           10,
  },
  scrambleLabel: {
    fontSize: '0.875rem',
    fontWeight:    800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color:         '#2D6A4F',
  },
  scrambleLetters: {
    display:       'flex',
    flexWrap:      'wrap' as const,
    justifyContent: 'center',
    gap:           6,
  },
  scrambleLetter: {
    fontFamily:    "'Cormorant Garamond', serif",
    fontSize:      '1.8rem',
    fontWeight:    700,
    color:         '#1B4332',
    background:    '#FFFFFF',
    borderRadius:  8,
    width:         44,
    height:        48,
    display:       'flex',
    alignItems:    'center',
    justifyContent: 'center',
    border:        '1.5px solid #B7E4C7',
  },

  questionPrompt: {
    fontSize:   '1.5rem',
    fontWeight: 700,
    color:      '#6B5C50',
    textAlign:  'center',
    margin:     0,
  },

  // Options grid (easy / medium)
  optionsGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap:                 12,
    width:               '100%',
  },
  optionBtn: {
    position:      'relative',
    display:       'flex',
    alignItems:    'center',
    justifyContent: 'center',
    gap:           8,
    width:         '100%',
    minHeight:     72,
    padding:       '16px 18px',
    background:    '#FFFFFF',
    border:        '1.5px solid #EFE7D7',
    borderRadius:  14,
    cursor:        'pointer',
    fontFamily:    "'Cormorant Garamond', serif",
    fontSize:      '1.75rem',
    fontWeight:    700,
    color:         '#2E1F14',
    textAlign:     'center',
    boxShadow:     '0 2px 8px rgba(27,67,50,0.06)',
    transition:    'all 0.15s ease',
  },
  optionBtnWrong: {
    background:  '#B5541A',
    border:      '2px solid #A04010',
    color:       '#FFFFFF',
    cursor:      'default',
    boxShadow:   '0 2px 12px rgba(181,84,26,0.25)',
  },
  optionBtnCorrect: {
    background:  'linear-gradient(135deg, #52B788, #3D9970)',
    border:      '2px solid #2D6A4F',
    color:       '#FFFFFF',
    boxShadow:   '0 4px 18px rgba(82,183,136,0.3)',
  },
  optionX: {
    fontSize:   '0.9rem',
    fontWeight: 800,
    opacity:    0.85,
  },
  optionCheck: {
    fontSize:   '0.9rem',
    fontWeight: 800,
    opacity:    0.9,
  },

  // Hard mode
  hardArea: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'stretch',
    gap:           14,
    width:         '100%',
  },
  hintRow: {
    display:        'flex',
    gap:            10,
    justifyContent: 'center',
    flexWrap:       'wrap',
  },
  hintBtn: {
    fontSize:     '1.25rem',
    fontWeight:   700,
    color:        '#1B4332',
    background:   '#E8F5EE',
    border:       '1.5px solid #B7E4C7',
    borderRadius: 10,
    padding:      '10px 16px',
    cursor:       'pointer',
    minHeight:    48,
    transition:   'all 0.15s ease',
  },
  hintBtnActive: {
    background:  '#1B4332',
    color:       '#FFFFFF',
    borderColor: '#1B4332',
  },
  hintResult: {
    fontSize:     '1.5rem',
    fontWeight:   600,
    color:        '#2D6A4F',
    background:   '#E8F5EE',
    border:       '1.5px solid #B7E4C7',
    borderRadius: 10,
    padding:      '10px 16px',
    textAlign:    'center',
  },
  inputRow: {
    display: 'flex',
    gap:     10,
    width:   '100%',
  },
  textInput: {
    flex:         1,
    fontSize:     '1.5rem',
    fontWeight:   600,
    color:        '#2E1F14',
    background:   '#FFFFFF',
    border:       '2px solid #EFE7D7',
    borderRadius: 12,
    padding:      '14px 16px',
    outline:      'none',
    minHeight:    64,
    fontFamily:   'Nunito, sans-serif',
    transition:   'border-color 0.2s ease',
  },
  textInputWrong: {
    borderColor: '#B5541A',
    background:  '#FFF5F0',
  },
  textInputCorrect: {
    borderColor: '#52B788',
    background:  '#F0FFF6',
  },
  submitBtn: {
    fontFamily:    'Nunito, sans-serif',
    fontSize:      '1rem',
    fontWeight:    800,
    color:         '#FFFFFF',
    background:    'linear-gradient(135deg, #1B4332, #2D6A4F)',
    border:        'none',
    borderRadius:  12,
    padding:       '14px 22px',
    cursor:        'pointer',
    minHeight:     56,
    letterSpacing: '0.02em',
    whiteSpace:    'nowrap',
    transition:    'opacity 0.15s ease',
  },
  wrongFeedback: {
    fontSize:   '0.95rem',
    fontWeight: 700,
    color:      '#B5541A',
    textAlign:  'center',
    margin:     0,
  },

  // ── Complete screen ──
  completeRoot: {
    minHeight:      '100vh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     'linear-gradient(160deg, #1B4332 0%, #2D6A4F 50%, #3D7A5F 100%)',
    padding:        '32px 20px',
    position:       'relative',
    overflow:       'hidden',
  },
  completeBg1: {
    position:      'absolute',
    top:           -100,
    right:         -100,
    width:         400,
    height:        400,
    borderRadius:  '50%',
    background:    'rgba(255,255,255,0.04)',
    pointerEvents: 'none',
  },
  completeBg2: {
    position:      'absolute',
    bottom:        -80,
    left:          -80,
    width:         320,
    height:        320,
    borderRadius:  '50%',
    background:    'rgba(212,160,23,0.07)',
    pointerEvents: 'none',
  },
  completeCard: {
    position:      'relative',
    zIndex:        1,
    background:    '#FFFFFF',
    borderRadius:  24,
    padding:       '48px 40px',
    maxWidth:      440,
    width:         '100%',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           20,
    boxShadow:     '0 24px 80px rgba(0,0,0,0.25)',
    textAlign:     'center',
  },
  completeCelebration: {
    fontSize:   '3.5rem',
    lineHeight: 1,
    animation:  'floatLeaf 3s ease-in-out infinite',
  },
  completeTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   '2.8rem',
    fontWeight: 700,
    color:      '#1B4332',
    lineHeight: 1.05,
  },
  completeSubtitle: {
    fontSize:   '1.05rem',
    color:      '#6B5C50',
    fontWeight: 500,
  },
  completeStats: {
    display:        'flex',
    alignItems:     'center',
    gap:            20,
    background:     '#FAF6EF',
    borderRadius:   14,
    padding:        '18px 24px',
    width:          '100%',
    justifyContent: 'center',
    border:         '1.5px solid #EFE7D7',
  },
  completeStat: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           4,
  },
  completeStatVal: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   '2rem',
    fontWeight: 700,
    color:      '#1B4332',
    lineHeight: 1,
  },
  completeStatLbl: {
    fontSize: '0.875rem',
    fontWeight:    700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color:         '#9A8070',
  },
  completeStatDivider: {
    width:      1.5,
    height:     36,
    background: '#E3D5C0',
  },
  completeBtns: {
    display:       'flex',
    flexDirection: 'column',
    gap:           10,
    width:         '100%',
    marginTop:     4,
  },
  completeBtnPrimary: {
    fontFamily:    'Nunito, sans-serif',
    fontSize:      '1.05rem',
    fontWeight:    800,
    color:         '#FFFFFF',
    background:    'linear-gradient(135deg, #1B4332, #2D6A4F)',
    border:        'none',
    borderRadius:  12,
    padding:       '15px 24px',
    cursor:        'pointer',
    minHeight:     52,
    letterSpacing: '0.02em',
  },
  completeBtnSecondary: {
    fontFamily:  'Nunito, sans-serif',
    fontSize:    '1rem',
    fontWeight:  700,
    color:       '#1B4332',
    background:  '#EFE7D7',
    border:      '1.5px solid #D8C9B8',
    borderRadius: 12,
    padding:     '13px 24px',
    cursor:      'pointer',
    minHeight:   48,
  },
  completeBtnGhost: {
    fontFamily: 'Nunito, sans-serif',
    fontSize:   '0.9rem',
    fontWeight: 700,
    color:      '#9A8070',
    background: 'transparent',
    border:     'none',
    cursor:     'pointer',
    padding:    '8px',
    minHeight:  40,
  },

  // ── Theme selector ──
  optionLabel: {
    fontFamily:   "'Cormorant Garamond', serif",
    fontSize:     '1.5rem',
    fontWeight:   600,
    color:        '#1B4332',
    marginBottom: 16,
  },
  themeRow: {
    display:   'flex',
    gap:       12,
    flexWrap:  'wrap' as const,
    marginTop: 12,
  },
  themeBtn: {
    display:       'flex',
    flexDirection: 'column' as const,
    alignItems:    'center',
    gap:           6,
    padding:       '14px 18px',
    background:    '#FFFFFF',
    border:        '2px solid #EFE7D7',
    borderRadius:  14,
    cursor:        'pointer',
    boxShadow:     '0 2px 10px rgba(27,67,50,0.06)',
    transition:    'all 0.15s ease',
  },
  themeBtnActive: {
    border:     '2.5px solid #2D6A4F',
    background: '#F0FAF4',
    boxShadow:  '0 4px 16px rgba(45,106,79,0.15)',
  },

  themeBtnLocked: { opacity: 0.55, cursor: 'pointer' as const },
  themeEmoji:     { fontSize: '1.8rem', lineHeight: 1 },
  themeCover: { width: 48, height: 48, objectFit: 'contain' as const, borderRadius: 8, background: '#fff' },
  themeLabel: { fontSize: '0.875rem', fontWeight: 700, color: '#2E1F14' },
  lockBadge: {
    position: 'absolute' as const,
    bottom: -4, right: -4,
    fontSize: '0.875rem',
    lineHeight: 1,
  },
}
